/**
 * PDP video loader v7 — warm-up near + seamless play
 *
 * Goals:
 *  - ZERO .mp4 on initial page load.
 *  - Begin buffering shortly BEFORE the user lands on the video (warm-up).
 *  - Autoplay instantly when the slide settles; loop, muted, playsinline, no controls.
 *  - Robust after section swaps (listens to `pdp:gallery:ready`).
 *
 * How it works:
 *  - Prebuffer stage: as you approach the slide (IO rootMargin) OR select a neighbour slide,
 *    we create a hidden <video>, set preload="auto", call load() (poster stays visible).
 *  - Reveal stage: when the video slide is selected (Flickity "settle") OR ≥ threshold visible,
 *    we hide poster, set autoplay, and play() immediately.
 *
 * Tunables:
 *   PREBUFFER_ROOT_MARGIN: how early to start buffer (px) before viewport.
 *   PLAY_THRESHOLD: intersection ratio to actually start playback (0.8 = 80%).
 *   NEIGHBOR_WARMUP: whether to warm previous/next on Flickity select.
 */

(function () {
  if (window.__PDPVideoLoaderV7) return;
  window.__PDPVideoLoaderV7 = true;

  // ---- Tunables ----
  const DEBUG = false;
  const PREBUFFER_ROOT_MARGIN = "200px 0px"; // backstop only - the Flickity
  // onSelect neighbor-warmup below is the primary trigger and is slide-index
  // exact (idx-1/idx+1). This IO is just a fallback for paths that don't go
  // through Flickity events (e.g. a raw scroll, or a jump that doesn't fire
  // "select" cleanly). At the measured ~664px cell width, 1500px used to
  // span ~2.26 cell-widths - enough for a video at gallery position 2 to
  // start prebuffering almost immediately on load, defeating the deferral
  // entirely. 200px (~0.3 cell-widths) only fires once a slide is nearly
  // adjacent to the viewport, so it no longer does that.
  const PLAY_THRESHOLD = 0.2;                 // was 0.8 yesterday; set to 0.6 if you want earlier start
  const NEIGHBOR_WARMUP = true;               // warm previous/next slides on Flickity select

  const log = (...a) => { if (DEBUG) console.log("[pdp-video v7]", ...a); };
  const $  = (s, r=document) => r.querySelector(s);
  const $$ = (s, r=document) => Array.from(r.querySelectorAll(s));
  const parseJSON = (str, fb) => { try { return JSON.parse(str); } catch { return fb; } };

  function findGallery(root) {
    return $(".js-product-gallery", root) ||
           $(".product-gallery__main", root) ||
           $(".product_gallery", root) ||
           root;
  }

  function buildVideoFrom(container) {
    const sources = parseJSON(container.getAttribute("data-sources") || "[]", []);
    if (!sources.length) return null;

    const poster = container.getAttribute("data-poster") || "";

    const v = document.createElement("video");
    v.className = "pdp-inline-video";
    v.muted = true;
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.loop = true;

    // Prebuffer now (we'll autoplay only at reveal)
    v.preload = "auto";

    if (poster) v.poster = poster;
    v.style.width = "100%";
    v.style.height = "100%";
    v.style.display = "block";
    v.style.objectFit = "cover";
    v.style.opacity = "0";                   // fade-in on reveal
    v.style.transition = "opacity 120ms linear";

    sources.forEach(s => {
      const srcEl = document.createElement("source");
      srcEl.src = s.src;
      if (s.type) srcEl.type = s.type;
      v.appendChild(srcEl);
    });

    return v;
  }

  // ---- Stage 1: Prebuffer (no autoplay; poster still visible) ----
  function ensurePrebuffer(cell) {
    if (!cell || cell.dataset.mediaType !== "video") return;
    if (cell.__pdpPrebuffered) return;

    const wrap = cell.querySelector('.product-video[data-video-type="html5"]');
    if (!wrap) return;

    const v = buildVideoFrom(wrap);
    if (!v) return;

    wrap.appendChild(v);
    try { v.load(); } catch (_) {}
    cell.__pdpVideoEl = v;
    cell.__pdpPrebuffered = true;
    log("prebuffer -> load()");
  }

  // ---- Stage 2: Reveal & Play ----
  function revealAndPlay(cell) {
    if (!cell || cell.dataset.mediaType !== "video") return;

    const wrap = cell.querySelector('.product-video[data-video-type="html5"]');
    if (!wrap) return;

    let v = cell.__pdpVideoEl;
    if (!v) {
      // If user jumped straight to it, build now
      v = buildVideoFrom(wrap);
      if (!v) return;
      wrap.appendChild(v);
      try { v.load(); } catch (_) {}
      cell.__pdpVideoEl = v;
      cell.__pdpPrebuffered = true;
    }

    // Hide poster image
    const posterImg = wrap.querySelector(".video-poster");
    if (posterImg) posterImg.style.display = "none";

    // Now turn on autoplay and start
    v.autoplay = true;
    const start = () => { v.style.opacity = "1"; v.play().catch(()=>{}); };
    if (typeof v.requestVideoFrameCallback === "function") {
      v.requestVideoFrameCallback(() => start());
    }
    v.addEventListener("loadeddata", start, { once: true });
    v.addEventListener("canplay", start, { once: true });
    setTimeout(start, 350); // guard

    cell.__pdpRevealed = true;
    log("reveal & play");
  }

  // ---- External embeds (YouTube/Vimeo) kept simple (mount at reveal) ----
  function paramsYouTube(u) {
    try {
      const url = new URL(u, location.href);
      let id = url.searchParams.get("v");
      if (!id && /youtu\.be/.test(url.hostname)) id = url.pathname.replace("/", "");
      url.searchParams.set("autoplay","1");
      url.searchParams.set("mute","1");
      url.searchParams.set("playsinline","1");
      url.searchParams.set("controls","0");
      url.searchParams.set("rel","0");
      if (id){ url.searchParams.set("loop","1"); url.searchParams.set("playlist", id); }
      return url.toString();
    } catch { return u; }
  }
  function paramsVimeo(u) {
    try {
      const url = new URL(u, location.href);
      url.searchParams.set("autoplay","1");
      url.searchParams.set("muted","1");
      url.searchParams.set("background","1");
      url.searchParams.set("playsinline","1");
      url.searchParams.set("loop","1");
      return url.toString();
    } catch { return u; }
  }
  function revealExternal(cell) {
    if (!cell || cell.dataset.mediaType !== "external_video") return;

    const wrap = cell.querySelector('.product-video[data-video-type="external"]');
    if (!wrap || wrap.dataset.mounted === "1") return;

    const raw = wrap.getAttribute("data-src");
    if (!raw) return;
    let src = raw;
    if (/youtube\.com|youtu\.be/.test(src)) src = paramsYouTube(src);
    if (/vimeo\.com/.test(src))            src = paramsVimeo(src);

    const posterImg = wrap.querySelector(".video-poster");
    if (posterImg) posterImg.style.display = "none";

    const iframe = document.createElement("iframe");
    iframe.src = src;
    iframe.allow = "autoplay; encrypted-media; picture-in-picture; fullscreen";
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute("frameborder", "0");
    iframe.loading = "lazy";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.display = "block";
    wrap.appendChild(iframe);

    wrap.dataset.mounted = "1";
    log("external reveal");
  }

  // ---- IntersectionObservers ----
  function setupObservers(gallery) {
    // Prebuffer slightly before visible
    const prebufferIO = new IntersectionObserver((entries) => {
      entries.forEach((ent) => {
        if (!ent.isIntersecting) return;
        const cell = ent.target.closest(".gallery-cell");
        ensurePrebuffer(cell);
        prebufferIO.unobserve(ent.target);
      });
    }, { root: null, rootMargin: PREBUFFER_ROOT_MARGIN, threshold: 0.01 });

    // Reveal/play when sufficiently visible (covers non-Flickity scroll paths)
    const playIO = new IntersectionObserver((entries) => {
      entries.forEach((ent) => {
        if (ent.intersectionRatio >= PLAY_THRESHOLD) {
          const cell = ent.target.closest(".gallery-cell");
          if (!cell) return;
          if (cell.dataset.mediaType === "video") revealAndPlay(cell);
          else if (cell.dataset.mediaType === "external_video") revealExternal(cell);
          playIO.unobserve(ent.target);
        }
      });
    }, { root: null, threshold: [PLAY_THRESHOLD] });

    // Observe posters inside gallery
    $$(".product-video .video-poster", gallery).forEach((img) => {
      prebufferIO.observe(img);
      playIO.observe(img);
    });
  }

  // ---- Flickity hooks: warm-up neighbors, reveal on settle ----
  const FLICKITY_WAIT_RETRY_MS = 100;
  const FLICKITY_WAIT_MAX_ATTEMPTS = 50; // ~5s - bounded so a gallery that never
                                          // gets Flickity (e.g. a genuinely single-
                                          // image product) doesn't poll forever.

  function attach(rootCtx) {
    const root = rootCtx || document;
    const gallery = findGallery(root);
    if (!gallery || gallery.__pdpVideoV7Attached) return;

    // setupObservers() registers IntersectionObservers - safe to run exactly
    // once per gallery, guarded separately from __pdpVideoV7Attached (see the
    // Flickity-readiness comment below). Deliberately NOT called eagerly here
    // at the top of attach(): this file runs on DOMContentLoaded, well before
    // Flickity (loaded async via jQuery) has necessarily applied its layout
    // transforms to the gallery cells. Until Flickity repositions them,
    // cells sit in their raw pre-carousel CSS position - which can place a
    // later slide's poster within reach of PREBUFFER_ROOT_MARGIN purely by
    // accident of that transient layout, firing ensurePrebuffer() via
    // unobserve()-and-lock-in, even though Flickity moves it off-screen a
    // moment later. So setupObservers() is only called once we know the
    // cells are in their FINAL position: either Flickity is confirmed ready
    // (below), or we've genuinely given up waiting on it (further below),
    // meaning no carousel repositioning is ever coming.
    const setupObserversOnce = () => {
      if (!gallery.__pdpVideoV7ObserversSet) {
        setupObservers(gallery);
        gallery.__pdpVideoV7ObserversSet = true;
      }
    };

    const flkty = (window.Flickity && window.Flickity.data) ? window.Flickity.data(gallery) : null;

    if (!flkty) {
      // Flickity hasn't initialized on this gallery yet (this file runs on
      // DOMContentLoaded, but Flickity - loaded via jQuery in
      // sections.js.liquid - isn't guaranteed to have finished by then).
      // Retry until gallery.classList has "flickity-enabled" (the same
      // signal the theme's own CSS already keys off, e.g. the LCP-placeholder
      // fade-out) or we give up after FLICKITY_WAIT_MAX_ATTEMPTS.
      //
      // This used to set __pdpVideoV7Attached = true unconditionally up
      // here, before this check - meaning if Flickity wasn't ready on the
      // very first call, the neighbor-warmup (onSelect) and reveal-on-settle
      // (onSettle) hooks below NEVER got attached, for the rest of the page's
      // life. The only thing actually working was the IntersectionObserver
      // fallback in setupObservers(), which triggers on raw pixel distance
      // from the viewport (PREBUFFER_ROOT_MARGIN), not on slide position -
      // which is why a video placed at slide 2 loaded immediately (comfortably
      // within that margin from slide 1) instead of waiting for the shopper
      // to actually approach it.
      gallery.__pdpVideoV7RetryCount = (gallery.__pdpVideoV7RetryCount || 0) + 1;
      if (gallery.__pdpVideoV7RetryCount <= FLICKITY_WAIT_MAX_ATTEMPTS) {
        setTimeout(() => attach(root), FLICKITY_WAIT_RETRY_MS);
      } else {
        // Genuinely giving up - no carousel repositioning is ever coming for
        // this gallery, so whatever position cells are in now IS final.
        // Safe to start observing.
        setupObserversOnce();
        log("gave up waiting for Flickity - neighbor-warmup/settle-reveal disabled, IntersectionObserver fallback still active");
      }
      return;
    }

    // Flickity is confirmed ready, meaning it has already applied its layout
    // transforms - cells are in their real, final carousel position now.
    setupObserversOnce();

    gallery.__pdpVideoV7Attached = true;

    const onSelect = () => {
      if (!NEIGHBOR_WARMUP) return;
      const cells = $$(".gallery-cell", gallery);
      const sel = $(".gallery-cell.is-selected", gallery);
      const idx = cells.indexOf(sel);
      // warm current + neighbours
      [idx - 1, idx + 1].forEach((i) => {
        const c = cells[i];
        if (!c) return;
        if (c.dataset.mediaType === "video") ensurePrebuffer(c);
      });
    };
    const onSettle = () => {
      const sel = $(".gallery-cell.is-selected", gallery);
      if (!sel) return;
      if (sel.dataset.mediaType === "video") revealAndPlay(sel);
      else if (sel.dataset.mediaType === "external_video") revealExternal(sel);
    };

    flkty.off("select", gallery.__pdpV7OnSelect);
    flkty.off("settle", gallery.__pdpV7OnSettle);
    gallery.__pdpV7OnSelect = onSelect;
    gallery.__pdpV7OnSettle = onSettle;

    flkty.on("select", onSelect);
    flkty.on("settle", onSettle);

    // first pass (in case first slide is video)
    onSelect();
    onSettle();
  }

  function init(){ attach(document); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();

  // After section swap reattach
  document.addEventListener("pdp:gallery:ready", (e) => {
    const root = (e.detail && e.detail.root) || document;
    attach(root);
  });
})();
